/**
 * Where everybody is in the race: overall progress for the progress strip and the view
 * picker, and the local player's place once the finish times (against the clock: the levels)
 * are known.
 */
import type { LevelData, PlayerId, PlayerSim, RaceProgressEntry } from '@cube-rush/shared';

/** How far along a level x is: 0 at the start, 1 at the finish line. */
export function levelFraction(level: Pick<LevelData, 'start' | 'length'>, x: number): number {
  const span = level.length - level.start.x;
  if (!(span > 0)) return 1;
  return Math.min(1, Math.max(0, (x - level.start.x) / span));
}

/** Overall race progress 0..1: completed levels plus the fraction of the current one. */
export function overallProgress(levelIndex: number, fraction: number, levelCount: number): number {
  if (levelCount <= 0) return 0;
  const value = (levelIndex + Math.min(1, Math.max(0, fraction))) / levelCount;
  return Math.min(1, Math.max(0, value));
}

/**
 * The local player's place (1 = winner) once the server has their finish time: one more
 * than the racers with a better time. Null before that. Equal times share the better place
 * (the server breaks such ties by arrival, which the client cannot see).
 */
export function finishPlace(progress: readonly RaceProgressEntry[], myId: PlayerId): number | null {
  const mine = progress.find((p) => p.id === myId)?.finishTm;
  if (mine === null || mine === undefined) return null;
  let place = 1;
  for (const entry of progress) {
    if (entry.id !== myId && entry.finishTm !== null && entry.finishTm < mine) place++;
  }
  return place;
}

/** Somebody besides `myId` has neither finished nor given up. */
export function othersStillRacing(progress: readonly RaceProgressEntry[], myId: PlayerId): boolean {
  return progress.some((p) => p.id !== myId && p.finishTm === null && !p.quit);
}

/**
 * Levels a run has completed: the one it is on does not count yet, the one in its short pause
 * before the next level (or at the finish) already does.
 */
export function completedLevels(sim: Pick<PlayerSim, 'levelIndex' | 'phase'>): number {
  const done = sim.phase === 'levelComplete' || sim.phase === 'finished';
  return sim.levelIndex + (done ? 1 : 0);
}

/**
 * The local player's place in a race against the clock as the server's progress has it: none
 * without a level; otherwise one more than the racers with more levels, or with as many done
 * earlier (`levelTm`). Equal times share the better place (the server breaks such ties by
 * arrival, which the client cannot see).
 */
export function timedPlace(progress: readonly RaceProgressEntry[], myId: PlayerId): number | null {
  const mine = progress.find((p) => p.id === myId);
  if (!mine || mine.lvl <= 0) return null;
  let place = 1;
  for (const entry of progress) {
    if (entry.id === myId) continue;
    const earlier = entry.levelTm !== null && mine.levelTm !== null && entry.levelTm < mine.levelTm;
    if (entry.lvl > mine.lvl || (entry.lvl === mine.lvl && earlier)) place++;
  }
  return place;
}
